import React, { useState, useEffect } from 'react';
import { Clock, Copy, Check, Calendar, Zap, History, Settings } from 'lucide-react';
import { PageTracker } from '../../../components/PageTracker.tsx';
import '../../../styles/dev-utils-theme.css';

interface ConversionResult {
  label: string;
  value: string;
  format: string;
}

interface HistoryItem {
  id: string;
  input: string;
  inputType: string;
  timestamp: string;
  results: ConversionResult[];
}

function TimestampConverter() {
  const [inputValue, setInputValue] = useState('');
  const [inputType, setInputType] = useState<'unix' | 'iso' | 'date' | 'current'>('current');
  const [timezone, setTimezone] = useState('UTC');
  const [results, setResults] = useState<ConversionResult[]>([]);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [autoUpdate, setAutoUpdate] = useState(true);

  // Common timezones
  const timezones = [
    'UTC',
    'America/New_York',
    'America/Los_Angeles', 
    'America/Chicago',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Kolkata',
    'Australia/Sydney',
    'Pacific/Auckland'
  ];

  // Update current time every second when auto-update is enabled
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoUpdate) {
      interval = setInterval(() => {
        setCurrentTime(new Date());
        if (inputType === 'current') {
          convertTimestamp('current', '');
        }
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoUpdate, inputType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Parse different input formats
  const parseInput = (type: string, value: string): Date | null => {
    try {
      switch (type) {
        case 'unix':
          // Handle both seconds and milliseconds
          const num = parseFloat(value);
          if (isNaN(num)) return null;
          
          // If number is less than year 2000 in seconds, assume it's in seconds
          // Otherwise assume milliseconds
          const timestamp = num < 946684800 ? num * 1000 : num;
          return new Date(timestamp);
          
        case 'iso':
          return new Date(value);
          
        case 'date':
          return new Date(value);
          
        case 'current':
          return currentTime;
          
        default:
          return null;
      }
    } catch (err) {
      return null;
    }
  };

  // Format date to different formats
  const formatDate = (date: Date): ConversionResult[] => {
    const results: ConversionResult[] = [];

    try {
      // Unix timestamp (seconds)
      results.push({
        label: 'Unix Timestamp (seconds)',
        value: Math.floor(date.getTime() / 1000).toString(),
        format: 'number'
      });

      // Unix timestamp (milliseconds)
      results.push({
        label: 'Unix Timestamp (milliseconds)',
        value: date.getTime().toString(),
        format: 'number'
      });

      // ISO 8601
      results.push({
        label: 'ISO 8601 (UTC)',
        value: date.toISOString(),
        format: 'iso'
      });

      // Local timezone ISO
      const localISO = new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().slice(0, -1);
      results.push({
        label: 'ISO 8601 (Local)',
        value: localISO,
        format: 'iso'
      });

      // RFC 2822
      results.push({
        label: 'RFC 2822',
        value: date.toString(),
        format: 'rfc'
      });

      // UTC String
      results.push({
        label: 'UTC String',
        value: date.toUTCString(),
        format: 'utc'
      });

      // Locale String
      results.push({
        label: 'Locale String',
        value: date.toLocaleString(),
        format: 'locale'
      });

      // Custom formats
      results.push({
        label: 'Date Only (YYYY-MM-DD)',
        value: date.toISOString().split('T')[0],
        format: 'date'
      });

      results.push({
        label: 'Time Only (HH:MM:SS)',
        value: date.toTimeString().split(' ')[0],
        format: 'time'
      });

      // Relative time
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);

      let relativeTime = '';
      if (Math.abs(diffDays) > 0) {
        relativeTime = diffDays > 0 ? `${diffDays} days ago` : `in ${Math.abs(diffDays)} days`;
      } else if (Math.abs(diffHours) > 0) {
        relativeTime = diffHours > 0 ? `${diffHours} hours ago` : `in ${Math.abs(diffHours)} hours`;
      } else if (Math.abs(diffMinutes) > 0) {
        relativeTime = diffMinutes > 0 ? `${diffMinutes} minutes ago` : `in ${Math.abs(diffMinutes)} minutes`;
      } else {
        relativeTime = diffSeconds > 0 ? `${diffSeconds} seconds ago` : `in ${Math.abs(diffSeconds)} seconds`;
      }

      results.push({
        label: 'Relative Time',
        value: relativeTime,
        format: 'relative'
      });

      // Day of week and other info
      results.push({
        label: 'Day of Week',
        value: date.toLocaleDateString('en-US', { weekday: 'long' }),
        format: 'text'
      });

      results.push({
        label: 'Month Name',
        value: date.toLocaleDateString('en-US', { month: 'long' }),
        format: 'text'
      });

      // Unix epoch days
      results.push({
        label: 'Days since Unix Epoch',
        value: Math.floor(date.getTime() / (1000 * 60 * 60 * 24)).toString(),
        format: 'number'
      });

    } catch (err) {
      console.error('Error formatting date:', err);
    }

    return results;
  };

  // Convert timestamp
  const convertTimestamp = (type: string, value: string) => {
    setError(null);
    
    const date = parseInput(type, value);
    
    if (!date || isNaN(date.getTime())) {
      setError('Invalid timestamp format');
      setResults([]);
      return;
    }

    const formattedResults = formatDate(date);
    setResults(formattedResults);

    // Add to history (except for current time auto-updates)
    if (type !== 'current' || !autoUpdate) {
      const historyItem: HistoryItem = {
        id: Math.random().toString(36).substr(2, 9),
        input: type === 'current' ? 'Current Time' : value,
        inputType: type,
        timestamp: new Date().toLocaleString(),
        results: formattedResults
      };
      
      setHistory(prev => [historyItem, ...prev].slice(0, 20));
    }
  };

  // Handle input change
  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (value.trim()) {
      convertTimestamp(inputType, value);
    } else {
      setResults([]);
      setError(null);
    }
  };

  // Handle input type change
  const handleInputTypeChange = (type: 'unix' | 'iso' | 'date' | 'current') => {
    setInputType(type);
    setInputValue('');
    setResults([]);
    setError(null);
    
    if (type === 'current') {
      convertTimestamp('current', '');
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(id);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Load from history
  const loadFromHistory = (item: HistoryItem) => {
    if (item.inputType === 'current') {
      setInputType('current');
      setInputValue('');
      convertTimestamp('current', '');
    } else {
      setInputType(item.inputType as any);
      setInputValue(item.input);
      setResults(item.results);
    }
  };

  // Clear history
  const clearHistory = () => {
    setHistory([]);
  };

  // Get current timestamp in different formats for quick access
  const getCurrentTimestamps = () => {
    const now = new Date();
    return {
      unix: Math.floor(now.getTime() / 1000),
      unixMs: now.getTime(),
      iso: now.toISOString()
    };
  };

  const quickTimestamps = getCurrentTimestamps();

  // Initialize with current time on component mount
  useEffect(() => {
    if (inputType === 'current') {
      convertTimestamp('current', '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="dev-utils-page" style={{ minHeight: "100vh", background: "var(--lyra-color-surface-2)", fontFamily: "'DM Sans', system-ui, sans-serif", padding: "28px 28px 48px" }}>
      <PageTracker pageTitle="Timestamp Converter" category="dev-utils" />
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <Clock size={20} style={{ color: "var(--app-primary-mid)" }} />
          <span style={{ fontSize: 22, fontWeight: 600, color: "var(--lyra-color-text)", fontFamily: "DM Sans, system-ui, sans-serif" }}>Timestamp Converter</span>
        </div>
        <p style={{ fontSize: 13, color: "var(--app-text-2)", margin: 0 }}>Convert between different timestamp formats and timezones</p>
      </div>

      {/* Quick Access */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Quick Access - Current Time
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Unix Timestamp</div>
            <div className="flex items-center justify-between">
              <code className="text-sm font-mono text-gray-900 dark:text-white">{quickTimestamps.unix}</code>
              <button
                onClick={() => copyToClipboard(quickTimestamps.unix.toString(), 'quick-unix')}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                {copiedItem === 'quick-unix' ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Unix Milliseconds</div>
            <div className="flex items-center justify-between">
              <code className="text-sm font-mono text-gray-900 dark:text-white">{quickTimestamps.unixMs}</code>
              <button
                onClick={() => copyToClipboard(quickTimestamps.unixMs.toString(), 'quick-unix-ms')}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                {copiedItem === 'quick-unix-ms' ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">ISO 8601</div>
            <div className="flex items-center justify-between">
              <code className="text-xs font-mono text-gray-900 dark:text-white">{quickTimestamps.iso}</code>
              <button
                onClick={() => copyToClipboard(quickTimestamps.iso, 'quick-iso')}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                {copiedItem === 'quick-iso' ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Input Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Input Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Input Type
            </label>
            <select
              value={inputType}
              onChange={(e) => handleInputTypeChange(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="current">Current Time</option>
              <option value="unix">Unix Timestamp</option>
              <option value="iso">ISO 8601 / RFC 3339</option>
              <option value="date">Human Readable Date</option>
            </select>
          </div>

          {/* Timezone Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Timezone
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              {timezones.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>

          {/* Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Options
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={autoUpdate}
                  onChange={(e) => setAutoUpdate(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Auto-update current time</span>
              </label>
            </div>
          </div>
        </div>

        {/* Input Field */}
        {inputType !== 'current' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {inputType === 'unix' && 'Unix Timestamp (seconds or milliseconds)'}
              {inputType === 'iso' && 'ISO 8601 / RFC 3339 Format'}
              {inputType === 'date' && 'Human Readable Date'}
            </label>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={
                inputType === 'unix' ? '1640995200 or 1640995200000' :
                inputType === 'iso' ? '2022-01-01T00:00:00Z' :
                'January 1, 2022 or 2022-01-01'
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white font-mono"
            />
          </div>
        )}

        {inputType === 'current' && (
          <div className="text-center py-4">
            <Clock className="h-12 w-12 text-blue-600 mx-auto mb-2" />
            <p className="text-gray-600 dark:text-gray-400">
              Showing current time {autoUpdate ? '(auto-updating)' : '(static)'}
            </p>
            <p className="text-2xl font-mono text-gray-900 dark:text-white mt-2">
              {currentTime.toLocaleString()}
            </p>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Conversion Results
          </h2>
          <div className="space-y-3">
            {results.map((result, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {result.label}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 font-mono mt-1 break-all">
                    {result.value}
                  </div>
                </div>
                <button
                  onClick={() => copyToClipboard(result.value, `result-${index}`)}
                  className="ml-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  title="Copy to clipboard"
                >
                  {copiedItem === `result-${index}` ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <History className="h-5 w-5" />
                Conversion History ({history.length})
              </h2>
              <button
                onClick={clearHistory}
                className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                Clear History
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                  onClick={() => loadFromHistory(item)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      {item.inputType.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {item.timestamp}
                    </span>
                  </div>
                  <div className="text-sm">
                    <div className="text-gray-600 dark:text-gray-400 mb-1">
                      Input: <span className="font-mono">{item.input}</span>
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">
                      Converted to {item.results.length} formats
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Information Panel */}
      <div className="mt-6 bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          About Timestamps
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Supported Formats:</h4>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li><strong>Unix Timestamp:</strong> Seconds since January 1, 1970</li>
              <li><strong>Unix Milliseconds:</strong> Milliseconds since epoch</li>
              <li><strong>ISO 8601:</strong> International standard date format</li>
              <li><strong>RFC 2822:</strong> Internet date format</li>
              <li><strong>Human Readable:</strong> Natural language dates</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Features:</h4>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>• Real-time current time display</li>
              <li>• Multiple timezone support</li>
              <li>• Relative time calculations</li>
              <li>• Conversion history</li>
              <li>• Quick access to current timestamps</li>
              <li>• Copy to clipboard functionality</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TimestampConverter;
